import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@src/lib/ui/AlertDialog";
import { Button } from "@src/lib/ui/Button";

interface OpusAgentSendWhileRunningDialogProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  // An attachment is not ready: sending would be refused.
  confirmDisabled?: boolean;
  // Why, from the attachment gate ("Wait for your files to finish reading",
  // "Remove the file that couldn't be read to send").
  confirmDisabledReason?: string | null;
}

// The gate's reason as its own sentence (one already ending in punctuation
// keeps it; anything else gets a full stop).
const sentence = (text: string): string =>
  /[.…!?]$/.test(text) ? text : `${text}.`;

// Sending while a turn is RUNNING supersedes it server-side and the in-flight
// answer is lost, so the send waits for an explicit yes.
export const OpusAgentSendWhileRunningDialog: React.FC<
  OpusAgentSendWhileRunningDialogProps
> = ({
  open,
  onCancel,
  onConfirm,
  confirmDisabled = false,
  confirmDisabledReason = null,
}) => (
  <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
    <AlertDialogContent data-testid="opus-agent-send-while-running-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Stop the current answer?</AlertDialogTitle>
        <AlertDialogDescription>
          Ask Opus is still working on your last message. Sending now stops that
          answer and starts on this one instead.
          {confirmDisabled &&
            ` ${sentence(confirmDisabledReason ?? "Your attachments aren't ready yet")}`}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel asChild>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="opus-agent-send-while-running-cancel"
          >
            Keep waiting
          </Button>
        </AlertDialogCancel>
        <AlertDialogAction asChild>
          <Button
            onClick={onConfirm}
            disabled={confirmDisabled}
            data-testid="opus-agent-send-while-running-confirm"
          >
            Send anyway
          </Button>
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
